import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  Typography,
  Grid,
  Stack,
  Button,
  Skeleton,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Avatar,
  alpha,
} from "@mui/material";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import SavingsRoundedIcon from "@mui/icons-material/SavingsRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import {
  getBalance,
  getWalletSummary,
  getTransactions,
  getDownline,
} from "../api";

export default function Dashboard() {
  const navigate = useNavigate();
  const [balance, setBalance] = useState(null);
  const [summary, setSummary] = useState(null);
  const [recentTxns, setRecentTxns] = useState([]);
  const [downlineCount, setDownlineCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.allSettled([
      getBalance(),
      getWalletSummary(),
      getTransactions({ page: 1, limit: 5 }),
      getDownline(1, 1),
    ]).then(([bal, sum, txn, dl]) => {
      if (bal.status === "fulfilled") setBalance(bal.value.data);
      if (sum.status === "fulfilled") setSummary(sum.value.data);
      if (txn.status === "fulfilled")
        setRecentTxns(txn.value.data.transactions || []);
      if (dl.status === "fulfilled") setDownlineCount(dl.value.data.total || 0);
      setLoading(false);
    });
  }, []);

  const stats = [
    {
      label: "Main Balance",
      value: balance
        ? `₹${Number(balance.mainBalance || 0).toLocaleString("en-IN")}`
        : "—",
      icon: <AccountBalanceWalletRoundedIcon />,
      color: "#1c6b8c",
    },
    {
      label: "Commission Balance",
      value: balance
        ? `₹${Number(balance.commissionBalance || 0).toLocaleString("en-IN")}`
        : "—",
      icon: <SavingsRoundedIcon />,
      color: "#f59e0b",
    },
    {
      label: "Today's Credits",
      value: summary
        ? `₹${Number(summary.totalCredits || summary.todayCredits || 0).toLocaleString("en-IN")}`
        : "—",
      icon: <ArrowDownwardRoundedIcon />,
      color: "#22c55e",
    },
    {
      label: "Today's Debits",
      value: summary
        ? `₹${Number(summary.totalDebits || summary.todayDebits || 0).toLocaleString("en-IN")}`
        : "—",
      icon: <ArrowUpwardRoundedIcon />,
      color: "#ef4444",
    },
    {
      label: "My Downline",
      value: downlineCount,
      icon: <GroupsRoundedIcon />,
      color: "#0ea5e9",
    },
  ];

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Box>
          <Typography variant="h5">Dashboard</Typography>
          <Typography variant="body2" color="text.secondary">
            Overview of your wallet & network
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAddAltRoundedIcon />}
          onClick={() => navigate("/invite")}
          sx={{ borderRadius: 3 }}
        >
          Add User
        </Button>
      </Stack>

      <Grid container spacing={2} mb={3}>
        {stats.map((s, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card sx={{ p: 2, height: "100%" }}>
              {loading ? (
                <Skeleton
                  variant="rectangular"
                  height={48}
                  sx={{ borderRadius: 2 }}
                />
              ) : (
                <Stack direction="row" alignItems="center" gap={1.5}>
                  <Avatar
                    sx={{
                      bgcolor: alpha(s.color, 0.14),
                      color: s.color,
                      width: 40,
                      height: 40,
                    }}
                  >
                    {s.icon}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {s.label}
                    </Typography>
                    <Typography variant="h6" fontWeight={700} noWrap>
                      {s.value}
                    </Typography>
                  </Box>
                </Stack>
              )}
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card sx={{ p: 0, overflow: "hidden" }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            px: 3,
            py: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            Recent Transactions
          </Typography>
          <Button size="small" onClick={() => navigate("/transactions")}>
            View All
          </Button>
        </Stack>
        {loading ? (
          <Box sx={{ p: 3 }}>
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} height={40} sx={{ mb: 1 }} />
            ))}
          </Box>
        ) : recentTxns.length === 0 ? (
          <Box sx={{ p: 5, textAlign: "center" }}>
            <TrendingUpRoundedIcon
              sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
            />
            <Typography color="text.secondary">No transactions yet</Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 560 }}>
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentTxns.map((t) => {
                const txnType = t.type || t.txnType || "";
                const isCredit = txnType === "credit" || txnType === "CREDIT";
                return (
                  <TableRow key={t.id} hover>
                    <TableCell>
                      <Chip
                        size="small"
                        label={txnType.toUpperCase()}
                        color={isCredit ? "success" : "error"}
                        sx={{
                          fontWeight: 600,
                          fontSize: 11,
                          minWidth: 60,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      ₹{Number(t.amount || 0).toLocaleString("en-IN")}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "text.secondary",
                        maxWidth: 200,
                      }}
                    >
                      <Typography variant="body2" noWrap>
                        {t.description || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "text.secondary",
                        fontSize: 13,
                      }}
                    >
                      {new Date(t.created_at || t.createdAt).toLocaleDateString(
                        "en-IN",
                        {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        },
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
            </Table>
          </Box>
        )}
      </Card>
    </Box>
  );
}
